/**
 * AbilityAddBubble.js
 * Implements the AddBubble enemy ability
 */

import { Game, GameTimer } from "pixi_core";

import { Ability } from "./Ability";
import { Common } from "../Common";
import { Bubble } from "../Bubble";

import { shuffle, BubbleColor, BubblePool, ENEMY_ABILITY_PARTICLE_TRAVEL_TIME, NB_MAX_COLUMN } from "../../Constants";
import { GridElement } from "../GridElement";

export class AbilityAddBubble extends Ability {
    /**
     * @public
     * @param {Number} nbBubbles
     * @param {Number} bubbleType
     * @param {BubbleColor} lastShotColor
     * @param {{x: Number, y: Number}} startPos
     * @param {Boolean} dodgeFirstLine
     * @param {Function} callback
     */
    constructor(nbBubbles, bubbleType, lastShotColor, startPos, dodgeFirstLine, callback) {
        super();

        let connectedEmptyTiles = null;
        let firstLineEmptyTiles = null;
        // Choose randomly within connected empty tiles
        if (dodgeFirstLine) {
            connectedEmptyTiles = shuffle(Common.grid.getConnectedEmptyTiles(1, NB_MAX_COLUMN));
            firstLineEmptyTiles = shuffle(Common.grid.getConnectedEmptyTiles(0, 1));
        } else {
            connectedEmptyTiles = shuffle(Common.grid.getConnectedEmptyTiles(0, NB_MAX_COLUMN));
        }

        /** @private @type {Bubble[]} */
        this._bubbles = [];

        this._pickBubbles(connectedEmptyTiles, nbBubbles, bubbleType, lastShotColor, startPos);

        if (dodgeFirstLine && this._bubbles.length < nbBubbles) {
            let nbMissingBubbles = nbBubbles - this._bubbles.length;
            this._pickBubbles(firstLineEmptyTiles, nbMissingBubbles, bubbleType, lastShotColor, startPos);
        }

        // Spawn bubble instantly if there is no start position (and no particle)
        const delay = startPos && this._bubbles.length > 0 ? ENEMY_ABILITY_PARTICLE_TRAVEL_TIME : 0;

        /** @private @type {GameTimer} */
        this._timerParticlesDelay = Game.windows.getCurrentWindow().createTimer(delay);
        this._timerParticlesDelay.onComplete = () => {
            if (this._bubbles) {
                for (const bubble of this._bubbles) {
                    bubble.spawn(null, 1, false, true);
                }
            }

            if (callback) {
                callback();
            }
        };
    }

    /**
     * @private
     * @param {GridElement[]} connectedEmptyTiles
     * @param {Number} nbBubbles
     * @param {Number} bubbleType
     * @param {BubbleColor} lastShotColor
     * @param {{x: Number, y: Number}} startPos
     */
    _pickBubbles(connectedEmptyTiles, nbBubbles, bubbleType, lastShotColor, startPos) {
        for (let i = 0; i < connectedEmptyTiles.length; ++i) {
            const tile = connectedEmptyTiles[i];

            if (i < nbBubbles) {
                /** @type {{ color: BubbleColor, special: BubbleColor }} */
                let color = { color: bubbleType, special: bubbleType };

                if (bubbleType === BubbleColor.eMIMIC) {
                    color = { color: lastShotColor, special: BubbleColor.eUNDEFINED };
                }

                if (bubbleType === BubbleColor.eCHAMELEON) {
                    color = { color: BubbleColor.eRANDOM_EDITOR, special: BubbleColor.eCHAMELEON };
                }

                if (bubbleType === BubbleColor.eSHIELDED) {
                    color = { color: BubbleColor.eRANDOM_EDITOR, special: BubbleColor.eSHIELDED };
                }

                if (bubbleType === BubbleColor.eHEAL) {
                    color = { color: BubbleColor.eRANDOM_EDITOR, special: BubbleColor.eHEAL };
                }

                const bubble = Common.bubbleFactory.createBubble(
                    Common.grid.convertGridPosToScreenPos(tile.column, tile.row),
                    color,
                    { column: tile.column, row: tile.row },
                    BubblePool.eGRID
                );

                if (startPos && bubble) {
                    Common.particles.createEnemyAbilityParticle(startPos.x, startPos.y, bubble.x, bubble.y, null);
                }

                if (bubble) {
                    this._bubbles.push(bubble);
                    bubble.visible = false;
                }
            } else {
                break;
            }
        }
    }

    /**
     * @public
     */
    destroy() {
        if (this._timerParticlesDelay) {
            this._timerParticlesDelay.destroy();
            this._timerParticlesDelay = null;
        }

        // A level could be suddenly closed before added bubbles are inserted into the grid
        // Let's destroy them here
        if (this._bubbles) {
            for (const bubble of this._bubbles) {
                bubble.destroy();
            }
            this._bubbles = null;
        }
    }
}
